<script setup lang="ts">
import { Doughnut } from 'vue-chartjs';
import {
    Chart as ChartJS, Tooltip, Legend, ArcElement,
} from 'chart.js';
import { computed } from 'vue';
import type { DoughnutChartDataType, DoughnutChartOptionsType } from '@/types/ChartType';
import { createHtmlLegendPlugin } from '@/utils/chartLegendPlugin';
import { BChartSkeleton, BChartEmptyState } from '@/design-system';

type PropsType = {
    data: DoughnutChartDataType;
    options?: DoughnutChartOptionsType;
    height?: number;
    width?: number;
    loading?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    height: 260,
    width: undefined,
    loading: false,
});

ChartJS.register(Tooltip, Legend, ArcElement);

const defaultOptions = computed(
    (): DoughnutChartOptionsType => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    usePointStyle: true,
                    padding: 16,
                    font: {
                        family: 'Roboto, sans-serif',
                        size: 12,
                    },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: 'white',
                bodyColor: 'white',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                padding: 12,
            },
        },
    }),
);

const chartPlugins = computed(() => {
    const plugins = [];
    if (props.options?.plugins?.htmlLegend) {
        plugins.push(createHtmlLegendPlugin(props.options.plugins.htmlLegend));
    }

    return plugins;
});

const mergedOptions = computed(
    (): DoughnutChartOptionsType => ({
        ...defaultOptions.value,
        ...props.options,
        plugins: {
            ...defaultOptions.value.plugins,
            ...props.options?.plugins,
        },
    }),
);

const containerStyle = computed(() => ({
    height: `${props.height}px`,
    width: props.width ? `${props.width}px` : '100%',
}));

const hasData = computed(() => props.data.datasets.some(dataset => dataset.data.some(value => value > 0)));
</script>

<template>
    <div class="b-doughnut-chart">
        <BChartSkeleton
            v-if="props.loading"
            :height="props.height"
        />
        <BChartEmptyState
            v-else-if="!hasData"
            :min-height="props.height"
        />
        <div
            v-else
            :style="containerStyle"
        >
            <Doughnut
                :data="data"
                :options="mergedOptions"
                :plugins="chartPlugins"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.b-doughnut-chart {
    position: relative;
    max-width: 100%;
    overflow: hidden;
}
</style>
